import type { PersonalDataRow, ProfileRow } from '@/services/auth-client/types';
import type { PostalCodeAddress } from '@/services/postal-code/types';
import { ageOn } from '@/utils/helpers/dates';

import type { PersonalDataInput } from '../types';

/**
 * Dados pessoais da conta (DV-25, fila 2026-10-31): quem cria conta voluntária
 * ou é dono de conta institucional informa telefone, CPF, data de nascimento,
 * gênero e endereço pessoais.
 *
 * ⚠️ **Obrigatório no formulário, opcional no banco.** O banco só confere a
 * forma (CPF válido e único, data plausível): a versão publicada do app e o
 * socorro sem conta (DV-24) continuam criando perfil sem nada disto.
 */

/**
 * A idade a partir da qual não há aviso. **Não é idade mínima**: desde a
 * planilha de 2026-10-31 o menor de 18 pode ser voluntário, e o app só avisa que
 * a instituição pode pedir a autorização do responsável legal. A regra antiga
 * (RN-12 do app original, "voluntário só com 18") foi emendada na DV-25.
 */
export const ADULT_AGE = 18;

/** `true` se quem nasceu em `birthDate` (`yyyy-MM-dd`) ainda não fez 18 anos em `today`. */
export function isUnderage(birthDate: string, today: Date = new Date()): boolean {
  return ageOn(birthDate, today) < ADULT_AGE;
}

/** As colunas pessoais de `profiles`, como vêm do banco. */
export interface PersonalDataColumns {
  phone_number: string | null;
  cpf: string | null;
  birth_date: string | null;
  gender: string | null;
  postal_code: string | null;
  city: string | null;
  neighborhood: string | null;
  street: string | null;
  number: string | null;
}

/**
 * Os campos que o formulário exige, na ordem dele. O bairro fica de fora: cidade
 * pequena tem CEP único sem bairro nem rua no ViaCEP, e cobrar um bairro que não
 * existe trancaria o cadastro (é o mesmo critério do schema).
 */
const REQUIRED_COLUMNS = [
  'phone_number',
  'cpf',
  'birth_date',
  'gender',
  'postal_code',
  'city',
  'street',
  'number',
] as const satisfies readonly (keyof PersonalDataColumns)[];

/**
 * O perfil já tem tudo que a conta voluntária exige?
 *
 * Decide se o passo de voluntário pede os dados pessoais: quem acabou de criar a
 * conta voluntária já os informou e vai direto às funções; quem é solicitante e
 * resolveu virar voluntário (o "upgrade") ainda não, e informa ali.
 */
export function hasCompletePersonalData(row: PersonalDataColumns): boolean {
  return REQUIRED_COLUMNS.every((column) => Boolean(row[column]?.trim()));
}

type AutofilledAddressField = 'city' | 'neighborhood' | 'street';

/**
 * O que o CEP consultado preenche: só campo **vazio**. Quem digitou a rua à mão
 * e depois informou o CEP não perde a rua — e o ViaCEP nunca devolve número.
 */
export function addressFieldsToFill(
  address: PostalCodeAddress,
  current: Record<AutofilledAddressField, string>,
): [AutofilledAddressField, string][] {
  const fields: AutofilledAddressField[] = ['city', 'neighborhood', 'street'];

  return fields
    .filter((field) => address[field] && !current[field].trim())
    .map((field) => [field, address[field]]);
}

/** Dados pessoais como estão no perfil, crus (dígitos, data ISO). */
export interface StoredPersonalData {
  phoneNumber: string | null;
  cpf: string | null;
  birthDate: string | null;
  gender: string | null;
  postalCode: string | null;
  city: string | null;
  neighborhood: string | null;
  street: string | null;
  number: string | null;
}

export const EMPTY_STORED_PERSONAL_DATA: StoredPersonalData = {
  phoneNumber: null,
  cpf: null,
  birthDate: null,
  gender: null,
  postalCode: null,
  city: null,
  neighborhood: null,
  street: null,
  number: null,
};

export function mapRowToStoredPersonalData(profile: ProfileRow): StoredPersonalData {
  return {
    phoneNumber: profile.phone_number,
    cpf: profile.cpf,
    birthDate: profile.birth_date,
    gender: profile.gender,
    postalCode: profile.postal_code,
    city: profile.city,
    neighborhood: profile.neighborhood,
    street: profile.street,
    number: profile.number,
  };
}

/** Domínio → colunas de `profiles`. */
export function toPersonalDataRow(input: PersonalDataInput): PersonalDataRow {
  return {
    cpf: input.cpf,
    birth_date: input.birthDate,
    postal_code: input.postalCode,
    city: input.city,
    neighborhood: input.neighborhood,
    street: input.street,
    number: input.number,
  };
}
